import { useMemo } from "react";
import { View } from "react-native";
import { useTranslation } from "react-i18next";
import { DEFAULT_WEEK_START_DAY, weekStartNAgo, type Point } from "@heraion/shared";

import { BarChart } from "../charts/BarChart";
import { ChartHead } from "../charts/ChartHead";
import { useChartReadout } from "../charts/pointSelection";
import type { UiTheme } from "../theme";
import { makeTierOneStyles } from "./panelStyles";
import { dateFormatter } from "@heraion/shared";
import { useDateLocale } from "../primitives/DateFormatContext";

// PG-64 fetta 2: one week per bar, for Rhythm, Streak and volume by tag. The
// PG-63 row model (TierOneTrendRow's bars): a fixed header line names the
// series and reads the selected week, «value · week», the latest by default;
// a tap on a bar changes it, and the value is not repeated beside the name.
// Every bar prints its number and its week under it. The current week is the
// in-progress outline, its number printed like the others: it already counts.

export type WeekBarsCardProps = {
  theme: UiTheme;
  /** The series' name, left of the readout. */
  title: string;
  /** One value per calendar week, oldest first, the current week last. */
  values: ReadonlyArray<number>;
  nowMs: number;
  /** The athlete's week start (Profile.weekStartDay). */
  weekStartDay?: number;
  /** The number printed on a bar. */
  valueFormat: (y: number) => string;
  /** The value in the header and in each bar's spoken name. Defaults to
   *  `valueFormat`. */
  readoutFormat?: (y: number) => string;
  /** The last week is still running. */
  partialLast?: boolean;
};

export function WeekBarsCard({
  theme,
  title,
  values,
  nowMs,
  weekStartDay = DEFAULT_WEEK_START_DAY,
  valueFormat,
  readoutFormat,
  partialLast = false,
}: WeekBarsCardProps) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const styles = useMemo(() => makeTierOneStyles(theme), [theme]);
  const points = useMemo<Point[]>(() => {
    const day = dateFormatter(dateLocale, { day: "numeric", month: "short" });
    const last = values.length - 1;
    return values.map((y, i) => {
      const x = weekStartNAgo(nowMs, weekStartDay, last - i);
      const label =
        partialLast && i === last
          ? t("mobile.progress.weekBars.thisWeek", { defaultValue: "this week so far" })
          : t("mobile.progress.weekBars.weekOf", {
              date: day.format(new Date(x)),
              defaultValue: "week of {{date}}",
            });
      return { x, y, label };
    });
  }, [values, nowMs, weekStartDay, dateLocale, partialLast, t]);
  const tick = useMemo(() => {
    const short = dateFormatter(dateLocale, { day: "numeric", month: "numeric" });
    return (p: Point) => short.format(new Date(p.x));
  }, [dateLocale]);
  const { selection, readout, readoutProps } = useChartReadout(
    points,
    readoutFormat ?? valueFormat,
    false,
  );
  if (readout === undefined) return null;
  return (
    <View style={styles.trendRow}>
      <ChartHead theme={theme} title={title} readout={readout} readoutProps={readoutProps} />
      <BarChart
        theme={theme}
        points={points}
        valueFormat={valueFormat}
        readoutFormat={readoutFormat}
        selection={selection}
        tick={tick}
        partialLast={partialLast}
      />
    </View>
  );
}
